import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import type { ReactNode } from "react";

import { sitePath, useSiteConfig } from "../hooks/use-site-config";
import { BrandMark } from "./brand-mark";
import { BreathingRings } from "./breathing-rings";
import { GoLink } from "./go-link";
import { ghostButton, smallButton } from "./section";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";
import { appConfig } from "@complete-athlete/enums";

const links = (slug: string | null) => [
  { to: sitePath(slug, "services"), label: m.landing_nav_services() },
  { to: sitePath(slug, "coaches"), label: m.landing_nav_coaches() },
  { to: sitePath(slug, "pricing"), label: m.landing_nav_pricing() },
  { to: sitePath(slug, "contact"), label: m.landing_nav_contact() },
];

const navLink =
  "shrink-0 rounded-full px-4 py-2 font-medium text-muted-foreground outline-none transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring";
const navLinkActive = { className: "text-foreground" };

export const SiteBrand = ({ className }: { className?: string }) => {
  const { slug } = useSiteConfig();

  return (
    <Link
      to={sitePath(slug)}
      className={cn("group flex items-center gap-2.5 text-lg font-extrabold tracking-[-0.04em] italic", className)}
    >
      <BrandMark className="transition-transform duration-300 ease-out group-hover:-rotate-6" />
      {appConfig.name}
    </Link>
  );
};

/** True once the page has moved off its top edge. */
const useScrolled = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const read = () => setScrolled(window.scrollY > 8);
    read();
    window.addEventListener("scroll", read, { passive: true });
    return () => window.removeEventListener("scroll", read);
  }, []);

  return scrolled;
};

/**
 * The bar every public page opens with. At the top of the page it has no surface of its own and
 * sits straight on the page; once the page scrolls it sticks and takes frosted glass behind it
 * (a near-solid card on phones, where blur is costly). On phones the links drop to a row that
 * scrolls sideways.
 */
export const SiteNav = () => {
  const scrolled = useScrolled();
  const { slug } = useSiteConfig();
  const navLinks = links(slug);

  return (
    <header
      data-scrolled={scrolled || undefined}
      className="sticky top-3 z-40 rounded-[28px] p-2.5 transition-[background-color,box-shadow,backdrop-filter] duration-200 data-scrolled:bg-card/95 md:data-scrolled:bg-card/60 md:data-scrolled:backdrop-blur-xl md:data-scrolled:backdrop-saturate-150 data-scrolled:shadow-[0_12px_32px_color-mix(in_oklch,var(--page-background)_70%,transparent)] md:rounded-full"
    >
      <div className="flex items-center gap-2 pl-2.5">
        <SiteBrand className="mr-auto md:mr-0" />
        <nav className="mx-auto hidden items-center gap-1 text-sm md:flex">
          {navLinks.map((link) => (
            <Link key={link.to} to={link.to} className={navLink} activeProps={navLinkActive}>
              {link.label}
            </Link>
          ))}
        </nav>
        <Link to="/login" className={cn(ghostButton, smallButton)}>
          {m.landing_nav_sign_in()}
        </Link>
        <GoLink to={sitePath(slug)} hash="times" size="small">
          {m.landing_nav_book()}
        </GoLink>
      </div>
      <nav className="mt-2 flex gap-1 overflow-x-auto text-sm [scrollbar-width:none] md:hidden [&::-webkit-scrollbar]:hidden">
        {navLinks.map((link) => (
          <Link key={link.to} to={link.to} className={navLink} activeProps={navLinkActive}>
            {link.label}
          </Link>
        ))}
      </nav>
    </header>
  );
};

/**
 * What a sub-page opens with: the bar, then the page's title set straight on the page with the
 * landing's rings behind it. No card: the cards start with the page's own content. Anything
 * passed as children (a row of jump links, a filter) sits under the title.
 */
export const SiteHead = ({ children, className }: { children: ReactNode; className?: string }) => (
  <>
    <SiteNav />
    <div className={cn("relative isolate overflow-x-clip px-4 pb-9 md:px-6 md:pb-12", className)}>
      <BreathingRings className="-top-[150px] -right-[220px] size-[620px] [mask-image:linear-gradient(to_bottom,black_45%,transparent_92%)] opacity-50 md:-right-[90px] md:opacity-100" />
      {children}
    </div>
  </>
);

/** The title band sub-pages put inside `SiteHead`: one very large word, one line, one way to book. */
export const PageTitle = ({ title, aside }: { title: ReactNode; aside?: ReactNode }) => {
  const { slug } = useSiteConfig();

  return (
    <div className="pt-14 md:pt-[clamp(72px,14vh,150px)]">
      <h1 className="text-[clamp(3.5rem,11vw,10rem)] leading-[0.88] font-semibold tracking-[-0.06em]">{title}</h1>
      <div className="mt-7 flex flex-wrap items-center gap-x-8 gap-y-5 md:mt-9">
        <GoLink to={sitePath(slug)} hash="times">
          {m.landing_nav_book()}
        </GoLink>
        {aside ? <p className="max-w-[340px] text-[15px] text-muted-foreground">{aside}</p> : null}
      </div>
    </div>
  );
};
